import { CaretLeft, CaretRight, CalendarBlank } from "@phosphor-icons/react";

/**
 * Week selector — lets owners pick which specific week they're editing.
 * Schedules are date-specific (no auto-repeat), so the label + arrows make
 * that intent unambiguous. `value` and `onChange` are ISO "YYYY-MM-DD"
 * strings for Monday of the selected week.
 */
function mondayOf(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  d.setDate(d.getDate() - day);
  return d;
}

export function isoMonday(date = new Date()) {
  const m = mondayOf(date);
  const y = m.getFullYear();
  const mo = String(m.getMonth() + 1).padStart(2, "0");
  const da = String(m.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

/**
 * The Monday of the week the owner is most likely planning *right now*.
 *
 * On Sunday the current calendar week (Mon–Sun) is essentially over —
 * owners are planning the week ahead. We advance the default to that
 * upcoming Monday so the editor opens already pointed at the next week
 * (avoids the Sunday-evening trap of editing the week that just ended).
 *
 * Mon–Sat: returns this calendar week's Monday (unchanged behavior).
 * Sun:     returns tomorrow's Monday.
 */
export function defaultPlanningWeekStart() {
  const now = new Date();
  if (now.getDay() === 0) {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return isoMonday(tomorrow);
  }
  return isoMonday(now);
}

function parseIsoDate(iso) {
  // Parse "YYYY-MM-DD" as a local date (no timezone shift).
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function formatRange(mondayIso) {
  const d0 = parseIsoDate(mondayIso);
  const d1 = new Date(d0);
  d1.setDate(d1.getDate() + 6);
  const monthFmt = (d) => d.toLocaleString(undefined, { month: "short" });
  if (d0.getMonth() === d1.getMonth()) {
    return `${monthFmt(d0)} ${d0.getDate()}–${d1.getDate()}, ${d0.getFullYear()}`;
  }
  return `${monthFmt(d0)} ${d0.getDate()} – ${monthFmt(d1)} ${d1.getDate()}, ${d0.getFullYear()}`;
}

export default function WeekSelector({ value, onChange, disabled = false }) {
  const defaultWeek = defaultPlanningWeekStart();
  const current = value || defaultWeek;
  const isDefaultWeek = current === defaultWeek;
  // On Sundays the default already points at next week, so the reset
  // button should read accordingly.
  const isSunday = new Date().getDay() === 0;

  const shift = (deltaDays) => {
    const d = parseIsoDate(current);
    d.setDate(d.getDate() + deltaDays);
    onChange(isoMonday(d));
  };

  return (
    <div
      className="rounded-2xl border border-border bg-card p-3 sm:p-4 flex flex-wrap items-center gap-2 sm:gap-3"
      data-testid="week-selector"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground">
        <CalendarBlank weight="duotone" className="h-4 w-4 text-primary" />
        Schedule for
      </div>
      <button
        type="button"
        onClick={() => shift(-7)}
        disabled={disabled}
        className="h-9 w-9 grid place-items-center rounded-full border border-border hover:bg-muted disabled:opacity-50"
        aria-label="Previous week"
        data-testid="week-selector-prev"
      >
        <CaretLeft weight="bold" className="h-4 w-4" />
      </button>
      <div
        className="flex-1 min-w-[180px] text-center font-display font-semibold text-base sm:text-lg"
        data-testid="week-selector-label"
      >
        Week of {formatRange(current)}
      </div>
      <button
        type="button"
        onClick={() => shift(7)}
        disabled={disabled}
        className="h-9 w-9 grid place-items-center rounded-full border border-border hover:bg-muted disabled:opacity-50"
        aria-label="Next week"
        data-testid="week-selector-next"
      >
        <CaretRight weight="bold" className="h-4 w-4" />
      </button>
      {!isDefaultWeek && (
        <button
          type="button"
          onClick={() => onChange(defaultWeek)}
          disabled={disabled}
          className="h-9 px-3 text-xs font-medium rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="week-selector-today"
        >
          {isSunday ? "Jump to next week" : "Jump to this week"}
        </button>
      )}
    </div>
  );
}
